import React, { useState, useRef, useContext, useEffect } from "react";
import Select from 'react-select';
import { Calendar } from "primereact/calendar";
import { useTranslation } from "react-i18next";
import { DataContext } from "../App";
import { useNavigate } from 'react-router-dom';

const BookNow = () => {
  const { t } = useTranslation();
  const context = useContext(DataContext);
  const navigate = useNavigate();

  const [destination, setDestination] = useState('');
  const [checkInDate, setCheckinDate] = useState(null);
  const [checkOutDate, setCheckOutDate] = useState(null);
  const [guests, setGuests] = useState(0);
  const [canSearch, setCanSearch] = useState(false);

  const modalRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.matchMedia("screen and (max-width: 767px)").matches);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.matchMedia("screen and (max-width: 767px)").matches);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  useEffect(() => {
    const handleScroll = () => {
      if (!isMobile && modalRef.current) {
        const modalBottom = modalRef.current.getBoundingClientRect().bottom;

        if (modalBottom <= 0) {
          setIsModalOpen(false);
        }
      }
    };
    const handleClickOutside = (event) => {
        if (!isCalendarOpen) {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
          setIsModalOpen(false);
        }
      };
    }
    

    window.addEventListener("scroll", handleScroll); 
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobile, isCalendarOpen]);

  const areaOptions = context.properties && context.properties.filter(x => x.status === "active").reduce((acc, property) => {
    if (!acc.some(option => option.value === property.address.area)) {
      acc.push({ value: property.address.area, label: property.address.area });
    }
    return acc;
  }, []);

  const guestOptions = Array.from(
    { length: context.properties && context.properties.filter(x => x.status === "active").reduce((max, property) => Math.max(max, property.guests || 1), 0) },
    (_, i) => ({ value: i + 1, label: (i + 1).toString() })
  );

  const toggleBookingModal = () => {
    setIsModalOpen(prevState => !prevState);
  };

  useEffect(() => {
    if (destination && checkInDate && checkOutDate && guests) {
      setCanSearch(true);
    }
  }, [destination, checkInDate, checkOutDate, guests]);

  const addDays = (date, days) => {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
};

  return (
    <>
      <button
        className={`book-now-btn ${isModalOpen ? 'modal-open' : ''}`}
        onClick={toggleBookingModal}
      >
        {t("BookNow")}
      </button>

      {isModalOpen && (
        <div className={`${isMobile ? "book-now-modal-mobile top-0 start-0 h-100 background-white z-index-1050" : "book-now-modal"}`} id="book-now" ref={modalRef}>
          <div className={`${isMobile ? "modal-content-mobile  p-4" : "modal-content"}`}>
            {isMobile && (
              <button
                className="close-modal-btn position-absolute top-0 end-0 m-3 btn btn-link text-dark"
                onClick={toggleBookingModal}
              >
                &times;
              </button>
            )}

            <h4 className={`mb-4 pb-3 text-center text-black title text-uppercase`}> {t("BookStay")}</h4>

            <label className={`label-book-modal mb-2 text-black text-uppercase`}>{t("Destination")}</label>
            <Select
              value={areaOptions && destination ? areaOptions.find(x => x.value === destination) : null}
              onChange={(e) => setDestination(e.value)}
              className={`w-100 custom-select-bookNow ${isMobile ? "mb-5" : "mb-5 mb-lg-0"}`}
              options={areaOptions}
              placeholder={t("SelectDestination")}
            />

            <div className={isMobile ? "mb-4" : "row mb-2 mt-4"}>
              <div className={isMobile ? "mb-4" : "col-12 col-md-6"}>
                <label className={`label-book-modal mb-1 text-black text-uppercase`}>{t("Checkin")}</label>
                <Calendar
                  value={checkInDate}
                  onChange={(e) => {
                    setCheckinDate(e.value);
                    setCheckOutDate(null);                    
                    setIsCalendarOpen(false);
                  }}
                  onFocus={() => setIsCalendarOpen(true)}
                  inputClassName={`custom-select-bookNow input-calendar-custom ${isMobile ? "w-100" : "w-full my-0 pb-0"}`}
                  className={`mb-4 ${isMobile ? "w-100" : "w-full custom-select custom-select-bookNow mb-lg-0 my-2"}`}
                  placeholder={t('dd/mm/yy')}
                  dateFormat="dd/mm/yy"
                  selectionMode="single"
                  minDate={addDays(new Date(Date.now()), 1)}
                  viewDate={checkInDate ? checkInDate : new Date(Date.now())}
                  readOnlyInput={true}
                />
              </div>

              <div className={isMobile ? "mb-4" : "col-12 col-md-6"}>
                <label className={`label-book-modal mb-1 text-black text-uppercase`}>{t("Checkout")}</label>
                <Calendar
                  value={checkOutDate}
                  onChange={(e) => {
                    setCheckOutDate(e.value);
                    setIsCalendarOpen(false);
                  }}
                  onFocus={() => setIsCalendarOpen(true)}
                  inputClassName={`custom-select-bookNow input-calendar-custom ${isMobile ? "w-100" : "w-full my-0 pb-0"}`}
                  className={`mb-4 ${isMobile ? "w-100" : "w-full custom-select custom-select-bookNow mb-lg-0 my-2"}`}
                  placeholder={t('dd/mm/yy')}
                  dateFormat="dd/mm/yy"
                  selectionMode="single"
                  minDate={addDays(checkInDate, 1)}
                  viewDate={checkOutDate ? checkOutDate : checkInDate}
                  readOnlyInput={true}
                />
              </div>
            </div>

            <label className={`label-book-modal text-black mb-2 text-uppercase ${isMobile ? "" : "mt-4"}`}>{t("NumberOfGuest")}</label>
            <Select
              value={guestOptions && guests ? guestOptions.find(x => x.value === guests) : null}
              isSearchable={false}
              onChange={(e) => {
                setGuests(e.value)
              }}
              className={`guests-bookNow-placeholder custom-select-bookNow ${isMobile ? "w-100 mb-4" : "w-full mb-5 mb-lg-0"}`}
              options={guestOptions}
              placeholder={t("AddGuestSelect")}
            />

            <button disabled={!canSearch} onClick={(e) => {
              e.preventDefault();
              setIsModalOpen(prevState => !prevState);
              navigate("/property-collection", { state: { searchedCheckIn: checkInDate, searchedCheckOut: checkOutDate, searchedGuests: guests, searchedDestination: destination } });
            }}
              className={`${isMobile ? "btn btn-dark w-100 py-2 text-uppercase" : "text-uppercase background-black mt-4"}`}>
              {t("CheckAvailability")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default BookNow;
